import {
  call, select, put, fork,
} from 'redux-saga/effects';
import actionTypes from 'store/postflop/actionTypes';
import api from 'store/api';
import { toast } from 'react-toastify';
import { URL } from '../../../constants/url';
import { stripHandToRanks } from '../../../utils/postflopHand';
import {
  normalizePostflopGame,
  resolvePostflopDataLocation,
} from '../../../utils/postflopPosition';
import {
  getTableSize, getStackSize, getPositionType, getPosition,
  getResultsPositionType,
  getResultsPosition,
  getBoardType,
  getSelectedBoard,
  getSelectedHandType,
  getPageMode,
} from '../selectors';
import {
  getCombo, getNextCombo, setSearchValue,
} from '../actions';
import { getRequestRevision, isCurrentRequest, requestMeta } from '../requestState';
import watchRequest from './requestLifecycle';

export function* getComboRequest(payload = {}, forceResultsMode = false) {
  const game = yield select(getTableSize);
  const bb = yield select(getStackSize);
  const positionType = yield select(getPositionType);
  const position = yield select(getPosition);
  const resultsPositionType = yield select(getResultsPositionType);
  const resultsPosition = yield select(getResultsPosition);
  const boardType = yield select(getBoardType);
  const selectedBoard = yield select(getSelectedBoard);
  const selectedHandType = yield select(getSelectedHandType);
  const pageMode = yield select(getPageMode);
  const currentStep = Array.isArray(payload.currentStep) ? payload.currentStep : ['starting'];
  const prevActions = currentStep.join('-');
  const isResultsMode = forceResultsMode || pageMode?.value === 'Results';
  const isPlo5Request = typeof payload.isPlo5 === 'boolean'
    ? payload.isPlo5
    : sessionStorage.getItem('isPlo5') === 'true';
  const isHuChallenge = isResultsMode && String(game).toUpperCase() === 'HU';
  const normalizedGame = normalizePostflopGame(game, isPlo5Request);
  const requestPositionType = isResultsMode ? resultsPositionType?.value : positionType;
  const requestPosition = isResultsMode ? resultsPosition?.value : position[0];
  const challengePosition = isHuChallenge ? 'SBvsBB' : resultsPosition?.value;
  const dataLocation = resolvePostflopDataLocation({
    game,
    isPlo5: isPlo5Request,
    bb,
    positionType: requestPositionType,
    spot: requestPosition,
  });

  const normalRequest = {
    method: 'post',
    url: isPlo5Request ? URL.PLO5_POSTFLOP_COMBO : URL.POSTFLOP_COMBO,
    data: {
      bb,
      game: dataLocation.game,
      position_type: dataLocation.positionType,
      position: dataLocation.position,
      board: payload.board || selectedBoard,
      previous_actions: prevActions,
      hand_combo_type: selectedHandType?.value || '',
    },
  };

  if (!isResultsMode) {
    return { request: normalRequest, fallbackRequests: [] };
  }

  return {
    request: {
      method: 'post',
      url: isPlo5Request
        ? URL.PLO5_POSTFLOP_COMBO_RESULTS
        : URL.POSTFLOP_COMBO_RESULTS,
      data: {
        bb,
        game: normalizedGame,
        position_type: resultsPositionType?.value,
        position: challengePosition,
        previous_actions: isHuChallenge ? prevActions : '',
        board_type: boardType,
        hand_combo_type: selectedHandType?.value || '',
        // Dealing or prefetching does not count as an answered hand. The
        // session endpoint records answers and flushes the final batch on Stop.
        challenge_results: true,
        wrong: null,
      },
    },
    // Challenge must use the endpoint that records results for this exact category.
    fallbackRequests: [],
  };
}

function* requestCombo(payload = {}, forceResultsMode = false) {
  const { request } = yield call(
    getComboRequest,
    payload,
    forceResultsMode,
  );

  const response = yield call(api, request);
  if (typeof response?.data?.combo !== 'string' || !response.data.combo.trim()) {
    throw new Error('No training hand is available for this spot. Please try another selection.');
  }
  return response;
}

export function* getComboGen({ payload = {} }) {
  const revision = yield select(getRequestRevision('combo'));
  const meta = requestMeta('combo', revision);
  try {
    const res = yield call(requestCombo, payload, false);
    if (!(yield select(isCurrentRequest('combo', revision)))) return;
    const { data } = res;

    yield put({ ...getCombo.success(data), meta });
    yield put(setSearchValue(stripHandToRanks(data.combo)));
  } catch (e) {
    if (!(yield select(isCurrentRequest('combo', revision)))) return;
    const message = e.message || String(e);
    yield put({ ...getCombo.error(message), meta });
    yield put({ type: actionTypes.SET_GAME_STARTED, payload: false, meta });
    yield call(toast.error, message);
  }
}

export function* getNextComboGen({ payload = {} }) {
  const revision = yield select(getRequestRevision('nextCombo'));
  const meta = requestMeta('nextCombo', revision);
  try {
    const res = yield call(requestCombo, payload, true);
    if (!(yield select(isCurrentRequest('nextCombo', revision)))) return;
    yield put({ ...getNextCombo.success(res.data), meta });
  } catch (e) {
    if (!(yield select(isCurrentRequest('nextCombo', revision)))) return;
    const message = e.message || String(e);
    yield put({ ...getNextCombo.error(message), meta });
    yield call(toast.error, message);
  }
}

export default function* listener() {
  yield fork(watchRequest, 'combo', getComboGen);
  yield fork(watchRequest, 'nextCombo', getNextComboGen);
}
